import { ChevronLeft } from '@mui/icons-material';
import { Box, ButtonBase, Skeleton } from '@mui/material';
import {
  useAssetsByIds,
  useCase,
  useConfirmCaseAssets,
} from '@peritasai/repository';
import type { CatalogItemGroupEntity } from '@peritasai/repository';
import type { SelectedCartDto } from '@peritasai/types';
import { Typography } from '@peritasai/ui-components';
import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssetItemState } from './AssetItemRow';
import { CategoryCard, CategoryCardItem } from './CategoryCard';
import { ProgressCard, ProgressCategoryCount } from './ProgressCard';
import { VerifyAssetsTabs } from './VerifyAssetsTabs';
import { AuthService } from '../../../auth';
import { useAssetDetectionStatus } from '../hooks/useAssetDetectionStatus';
import {
  allocateChecklistSlots,
  ChecklistSlot,
  getReservedAssetIds,
} from '../utils/allocateChecklistSlots';
import { getVerifierName } from '../utils/getVerifierName';

const CASE_SCHEDULE_ROUTE = '/case-schedule';

interface CategoryDefinition {
  key: string;
  labelKey: string;
  groups: CatalogItemGroupEntity[];
}

interface CategorySlots {
  category: CategoryDefinition;
  slots: ChecklistSlot[];
}

/**
 * Asset ids to send to assign-batch on confirm: every detected asset that
 * isn't yet allocated to its slot, then the case's cart if it's in the room
 * and not already ALLOCATED — so a Build-Cart-reserved cart isn't left
 * RESERVED. The cart goes last, matching the backend's tray-then-cart order.
 */
export function getAssetIdsToAssign(
  slots: ChecklistSlot[],
  selectedCart: SelectedCartDto | null,
  isCartInRoom: boolean
): string[] {
  const assetIds = slots.flatMap((slot) =>
    slot.needsAssignment && slot.detectedAsset ? [slot.detectedAsset.id] : []
  );
  if (selectedCart && isCartInRoom && selectedCart.status !== 'ALLOCATED') {
    assetIds.push(selectedCart.assetId);
  }
  return assetIds;
}

/**
 * Reference number to show on a slot's row: the detected room asset's, else
 * (once confirmed) the assigned asset's, so it survives the asset leaving.
 */
function slotReferenceNumber(
  slot: ChecklistSlot,
  referenceNumberByAssetId: Map<string, string>
): string | undefined {
  if (slot.detectedAsset) return slot.detectedAsset.referenceNumber;
  return slot.assignedAssetId
    ? referenceNumberByAssetId.get(slot.assignedAssetId)
    : undefined;
}

/**
 * Every slot of a verified case is confirmed, even if its asset was since
 * reassigned elsewhere; otherwise the slot reflects live room detection.
 */
function slotState(slot: ChecklistSlot, isVerified: boolean): AssetItemState {
  if (isVerified) return 'confirmed';
  if (slot.detectedAsset) return 'detected';
  return 'loading';
}

export interface VerifyAssetsPageProps {
  caseId: string;
}

export function VerifyAssetsPage({ caseId }: VerifyAssetsPageProps) {
  const { t } = useTranslation(['periverse']);
  const navigate = useNavigate();
  const { data: caseEntity, isLoading: isCaseLoading } = useCase(caseId, {
    enableRealtime: true,
  });
  const confirmMutation = useConfirmCaseAssets();

  const buildChecklist = caseEntity?.buildChecklist;

  const categories: CategoryDefinition[] = useMemo(() => {
    if (!buildChecklist) return [];
    return [
      {
        key: 'trays',
        labelKey: 'pages.caseVerifyAssets.categories.trays',
        groups: buildChecklist.getTrays(),
      },
      {
        key: 'equipment',
        labelKey: 'pages.caseVerifyAssets.categories.equipment',
        groups: buildChecklist.getEquipment(),
      },
      {
        key: 'implants',
        labelKey: 'pages.caseVerifyAssets.categories.implants',
        groups: buildChecklist.getImplants(),
      },
    ];
  }, [buildChecklist]);

  const requiredCatalogItemIds = useMemo(
    () =>
      Array.from(
        new Set(
          categories.flatMap((category) =>
            category.groups.map((group) => group.catalogItemId)
          )
        )
      ),
    [categories]
  );

  // The cart is shown purely from the case's selected cart (not the
  // preference card) and tracked by asset id to tick it once in the room.
  const selectedCart = caseEntity?.selectedCart ?? null;
  const selectedCartAssetId = selectedCart?.assetId;
  const cartTrackedAssetIds = useMemo(
    () => (selectedCartAssetId ? [selectedCartAssetId] : []),
    [selectedCartAssetId]
  );

  const { assetsInRoom } = useAssetDetectionStatus(
    requiredCatalogItemIds,
    caseEntity?.locationId ?? null,
    cartTrackedAssetIds
  );

  const reservedAssetIds = useMemo(
    () =>
      getReservedAssetIds(categories.flatMap((category) => category.groups)),
    [categories]
  );

  const categorySlots: CategorySlots[] = useMemo(() => {
    return categories.map((category) => ({
      category,
      slots: allocateChecklistSlots(
        category.groups,
        assetsInRoom,
        reservedAssetIds
      ),
    }));
  }, [categories, assetsInRoom, reservedAssetIds]);

  const isVerified = !!caseEntity?.verifiedAt;

  // Confirmed assets may have left the room, so look up their reference
  // numbers directly rather than relying on room detection.
  const assignedAssetIds = useMemo(
    () => (isVerified ? Array.from(reservedAssetIds).sort() : []),
    [isVerified, reservedAssetIds]
  );
  const assignedAssetQueries = useAssetsByIds(assignedAssetIds);
  const referenceNumberByAssetId = new Map<string, string>();
  assignedAssetQueries.forEach((query, index) => {
    const assetId = assignedAssetIds[index];
    if (assetId && query.data) {
      referenceNumberByAssetId.set(assetId, query.data.referenceNumber);
    }
  });

  const cartName = selectedCart?.referenceNumber || undefined;
  const isCartInRoom =
    !!selectedCart &&
    assetsInRoom.some((asset) => asset.id === selectedCart.assetId);

  const allSlots = categorySlots.flatMap(({ slots }) => slots);

  const stateOf = (slot: ChecklistSlot) => slotState(slot, isVerified);
  const countSatisfied = (slots: ChecklistSlot[]) =>
    slots.filter((slot) => stateOf(slot) !== 'loading').length;

  const categoryCounts: ProgressCategoryCount[] = categorySlots.map(
    ({ category, slots }) => ({
      labelKey: category.labelKey,
      detected: countSatisfied(slots),
      total: slots.length,
    })
  );

  const totalCount = allSlots.length;
  const detectedCount = countSatisfied(allSlots);

  const handleConfirm = () => {
    const assetIds = getAssetIdsToAssign(allSlots, selectedCart, isCartInRoom);
    const verifiedBy = getVerifierName(AuthService.getCurrentUserProfile());
    confirmMutation.mutate({ caseId, assetIds, verifiedBy });
  };

  const handleBack = () => {
    navigate({ to: CASE_SCHEDULE_ROUTE });
  };

  if (isCaseLoading) {
    return (
      <Box sx={{ padding: '24px' }} data-testid="verify-assets-loading">
        <Skeleton variant="text" width={240} height={40} />
        <Skeleton variant="rectangular" height={300} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!caseEntity) {
    return null;
  }

  if (!caseEntity.locationId) {
    return (
      <Box
        sx={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
        data-testid="verify-assets-no-room"
      >
        <Typography variant="h5">
          {t('pages.caseVerifyAssets.noRoomAssigned.title')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('pages.caseVerifyAssets.noRoomAssigned.description')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="verify-assets-page"
      sx={{
        overflow: 'auto',
        minHeight: '800px',
      }}
    >
      <Box
        sx={{
          padding: '16px 24px 0',
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        }}
      >
        <ButtonBase
          onClick={handleBack}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            color: 'primary.main',
            width: 'fit-content',
            padding: '4px 0',
          }}
        >
          <ChevronLeft sx={{ fontSize: 18 }} />
          <Typography
            variant="subtitle2"
            sx={{ color: 'primary.main', fontSize: '13px' }}
          >
            {t('pages.caseVerifyAssets.backButton')}
          </Typography>
        </ButtonBase>

        <Typography
          variant="h4"
          sx={{ fontSize: '32px', fontWeight: 700, lineHeight: 1.235 }}
        >
          {`${caseEntity.dateTime.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
          })} • ${caseEntity.surgeon}`}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {`${caseEntity.locationName} • ${caseEntity.caseType}`}
        </Typography>
      </Box>

      <VerifyAssetsTabs />

      {totalCount === 0 ? (
        <Box
          sx={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
          data-testid="verify-assets-empty"
        >
          <Typography variant="h5">
            {t('pages.caseVerifyAssets.emptyPreferenceCard.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('pages.caseVerifyAssets.emptyPreferenceCard.description')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            padding: '24px',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              flex: 1,
              maxWidth: '493px',
            }}
          >
            {categorySlots.map(({ category, slots }) => {
              const items: CategoryCardItem[] = slots.map((slot) => ({
                id: slot.checklistItemId,
                name: slot.name,
                assetId: slotReferenceNumber(slot, referenceNumberByAssetId),
                state: stateOf(slot),
              }));

              if (items.length === 0) return null;

              const categoryDetected = items.filter(
                (item) =>
                  item.state === 'detected' || item.state === 'confirmed'
              ).length;

              return (
                <CategoryCard
                  key={category.key}
                  titleKey={category.labelKey}
                  items={items}
                  detectedCount={categoryDetected}
                  totalCount={items.length}
                  cartName={category.key === 'trays' ? cartName : undefined}
                  isCartInRoom={category.key === 'trays' && isCartInRoom}
                  isVerified={isVerified}
                />
              );
            })}
          </Box>

          <ProgressCard
            detectedCount={detectedCount}
            totalCount={totalCount}
            categoryCounts={categoryCounts}
            isVerified={isVerified}
            verifiedByName={caseEntity.verifiedByName}
            verifiedAt={caseEntity.verifiedAt}
            onConfirm={handleConfirm}
            isConfirming={confirmMutation.isPending}
          />
        </Box>
      )}
    </Box>
  );
}
